import React, { useCallback, useContext, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Divider, Typography, Button, Stack, useMediaQuery } from '@mui/material';
import config from '@shared/config';
import { FeedbackContext, LocalizationContext } from '@cvt/contexts';
import { useRouter } from '@cvt/routing';
import { LoggedOutActionsLayout } from '@modules/Auth/components';
import { UserSignupForm } from '@modules/Auth/partials/UserSignupForm';
import { useSignup } from '../hooks/useSignup';


interface Props {
  onSignedUp?: () => void;
  onAlreadyHaveAccount?: () => void;
  userData?: {
      firstName: string,
      phone: string,
      email: string
    };
}

export const SignUp = ({ onSignedUp, onAlreadyHaveAccount, userData }: Props) => {
  const router = useRouter();
  const { genericErrorFeedback, triggerFeedback } = useContext(FeedbackContext);
  const { dictionary } = useContext(LocalizationContext);
  const { signupAndLogin, signupWithSSO } = useSignup();
  const [ssoLoading, setSsoLoading] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width:600px)');

  const onSSOLogin = useCallback(async (provider: string) => {
    setSsoLoading(provider);
    try {
      await signupWithSSO(provider);
      onSignedUp?.();
    } catch (e: any) {
      triggerFeedback({
        severity: 'error',
        message: e.message,
      });
    } finally {
      setSsoLoading(null);
    }
  }, [onSignedUp, signupWithSSO, triggerFeedback]);

  const onSubmit = useCallback(async (data: Auth.SignupWithEmailAndPassword) => {
    try {
      await signupAndLogin(data);
      onSignedUp?.();
    } catch (e) {
      console.error(e);
      genericErrorFeedback();
    }
  }, [signupAndLogin, onSignedUp, genericErrorFeedback]);

  return (
    <LoggedOutActionsLayout>
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          mx: 'auto',
          my: isMobile ? 1 : 4,
          py: isMobile ? 1 : 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h5" align="center" mb={isMobile ? 1.5 : 2}>
          {dictionary.auth.signUp.title}
        </Typography>

        <Stack
          spacing={1.2}
          width="100%"
          mb={1.2}
        >
          {config.featureFlags.googleSSO && (
            <Button
              onClick={() => onSSOLogin('google')}
              variant="contained"
              color="primary"
              disabled={ssoLoading === 'google'}
              startIcon={
                <img src="/google.svg" alt="Google logo" width={20} height={20} />
              }
              aria-label={dictionary.auth.signUp.buttonSignupWithGoogle}
              size="medium"
              fullWidth
              sx={{
                minWidth: 0,
                py: 1.2,
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: 1,
              }}
            >
              {dictionary.auth.signUp.buttonSignupWithGoogle}
            </Button>
          )}
          {config.featureFlags.metaSSO && (
            <Button
              onClick={() => onSSOLogin('facebook')}
              variant="contained"
              disabled={ssoLoading === 'facebook'}
              sx={{
                bgcolor: '#1877F2',
                color: '#fff',
                '&:hover': { bgcolor: '#1151A3' },
                minWidth: 0,
                py: 1.2,
                fontWeight: 600,
                fontSize: '1rem',
                boxShadow: 1,
              }}
              startIcon={
                <img src="/facebook.svg" alt="Facebook logo" width={20} height={20} />
              }
              aria-label={dictionary.auth.signUp.buttonSignupWithMeta}
              size="medium"
              fullWidth
            >
              {dictionary.auth.signUp.buttonSignupWithMeta}
            </Button>
          )}
        </Stack>

        <Divider
          sx={{
            width: '100%',
            my: isMobile ? 1.5 : 2.5,
            fontSize: '0.85rem',
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {dictionary.auth.signUp.orSignUpWithEmail || 'or sign up with email'}
          </Typography>
        </Divider>

        <Box width="100%" maxWidth={340} mb={isMobile ? 1 : 2}>
          <UserSignupForm
            onSubmitRequest={onSubmit}
            onSubmitButtonText={dictionary.auth.signUp.buttonSignup}
            defaultValues={userData}
          />
        </Box>

        <Typography variant="body2" mb={0.5}>
          {dictionary.auth.signUp.alreadyHaveAccount}
        </Typography>
        <Button
          {...(onAlreadyHaveAccount
            ? { onClick: onAlreadyHaveAccount }
            : { component: RouterLink, to: router.auth.login.path }
          )}
          variant="outlined"
          color="secondary"
          size="medium"
          sx={{ width: '100%', maxWidth: 340, mt: 0.7 }}
        >
          {dictionary.auth.signUp.buttonLogin}
        </Button>
      </Box>
    </LoggedOutActionsLayout>
  );
};
